import React from 'react';
import {
  Controller,
  useFieldArray,
  useFormContext,
  useFormState,
} from 'react-hook-form';
import { Input } from 'components';
import { Container, TimeWindowSumRow } from 'components/TimeWindow';
import { CenteredContent, Table } from 'components/Table/TableStyles';
import { Button, Plus } from 'components';
import { useWeightCalculations } from 'hooks/useWeightCalculation';
import { TimeWindowFormProps } from 'components/TimeWindow/TimeWindows';
import { LagDateBadge } from 'components/LagDateBadge/LagDateBadge';
import { useDefaultLagDate } from 'hooks/useDefaultLagDate';
import { ReturnComponentSearch } from 'components/Select/ReturnComponentSearch';
import { useFormErrors } from 'hooks';

export const StaticWeightedTimeWindow: React.FC<TimeWindowFormProps> = ({
  index,
}) => {
  const { trigger, register, control, setValue, watch, clearErrors } =
    useFormContext();
  const timeWindow = `timeWindows[${index}]`;
  const { errors } = useFormState({ name: timeWindow, control });
  const { defaultLagDate } = useDefaultLagDate();
  const { getErrorMessage } = useFormErrors({ errors, index });

  const lineItemsFieldName = `${timeWindow}.lineItems`;
  const weightSumFieldName = `${timeWindow}.weightSum`;

  const { fields, append, remove } = useFieldArray({
    control,
    name: lineItemsFieldName,
  });
  const watchFieldArray = watch(lineItemsFieldName);
  const lineItems = fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index],
    };
  });

  const { weightSum } = useWeightCalculations({
    weightSumFieldName,
    lineItems,
    setValue,
    trigger,
    errors,
  });
  const timeWindowLineItems = lineItems.map((lineItem, lineIndex) => {
    const { isLagged, customLagDate, name }: any = lineItem;

    return (
      <tr key={lineItem.id}>
        <td>
          <Controller
            control={control}
            name={`${lineItemsFieldName}[${lineIndex}].returnComponent`}
            render={({ field: { onChange, value } }) => {
              return (
                <ReturnComponentSearch
                  size='sm'
                  testid={`${lineItemsFieldName}.${lineIndex}.component-search`}
                  onChange={(val) => {
                    // clearErrors is used to cleanup server errors, that we show on UI
                    clearErrors(timeWindow);
                    const fieldName = `${lineItemsFieldName}[${lineIndex}]`;
                    setValue(`${fieldName}.isLagged`, val.isLagged);
                    setValue(`${fieldName}.customLagDate`, val.customLagDate);
                    setValue(`${fieldName}.name`, val?.name || val?.label);

                    onChange(val.value);
                    trigger(
                      `${lineItemsFieldName}[${lineIndex}].returnComponent`
                    );
                  }}
                  onBlur={() => {
                    trigger(
                      `${lineItemsFieldName}[${lineIndex}].returnComponent`
                    );
                  }}
                  value={
                    value
                      ? {
                          returnComponent: value,
                          value,
                          label: name,
                        }
                      : ''
                  }
                  hasError={getErrorMessage(
                    `${lineItemsFieldName}[${lineIndex}].returnComponent`
                  )}
                />
              );
            }}
          />
        </td>
        <td>
          <CenteredContent
            onInputCapture={() => {
              clearErrors(timeWindow);
              trigger(timeWindow);
            }}
          >
            <Input
              {...register(`${lineItemsFieldName}[${lineIndex}].weight`)}
              error={getErrorMessage(
                `${lineItemsFieldName}[${lineIndex}].weight`
              )}
              data-testid={`${lineItemsFieldName}.${lineIndex}.weight`}
              style={{ width: 125, marginRight: 3 }}
              size='sm'
            />
            %
          </CenteredContent>
        </td>
        <td className='lagDate'>
          {LagDateBadge({
            isLagged: isLagged,
            customLagDate: customLagDate,
            defaultLagDate: defaultLagDate,
          })}
        </td>
        <td>
          <CenteredContent>
            <Button
              data-testid={`${lineItemsFieldName}.${lineIndex}.remove-weight-component`}
              style={{ color: '#F8333C', fontSize: '12px' }}
              variant='tertiary'
              onClick={() => {
                remove(lineIndex);
                clearErrors(timeWindow);
                trigger(timeWindow);
              }}
            >
              Remove
            </Button>
          </CenteredContent>
        </td>
      </tr>
    );
  });

  return (
    <>
      <Container key={lineItemsFieldName}>
        <Table>
          <table className='timeWindowTable'>
            <tbody>
              <tr style={{ height: 40 }}>
                <th style={{ width: '50%' }}>Return Component</th>
                <th className='center'>Component Weight</th>
                <th className='left'>Lag Date</th>
                <th className='center'>Actions</th>
              </tr>
              {timeWindowLineItems}
              <tr key='add component'>
                <td align='left' colSpan={4}>
                  <Button
                    data-testid={`add-component-btn-${index}`}
                    style={{ height: '24px' }}
                    variant='secondary'
                    onClick={() =>
                      append({
                        returnComponent: '',
                        weight: '',
                        isLagged: false,
                      })
                    }
                  >
                    <Plus size='10px' />
                    Component
                  </Button>
                </td>
              </tr>
              <TimeWindowSumRow percentage={weightSum} />
            </tbody>
          </table>
        </Table>
      </Container>
    </>
  );
};
